import { useCallback } from "react";
import { IBulkBatch } from "@wingspanhq/payments/dist/interfaces";
import useSnackbar from "../../../hooks/useSnackbar";
import { downloadFileFromBlob } from "../../../utils/files";
import { BulkResource } from "../types";
import { getFlatfileMainSheetId } from "../utils/getFlatfileMainSheetId";
import { getUploadedFilename } from "../utils/getUploadedFilename";
import { useFlatfileApiClient } from "./useFlatfileApiClient";

function getSkippedRowsFilename(
  bulkBatch: IBulkBatch,
  bulkResource: BulkResource
): string {
  const uploadedFilename = getUploadedFilename(bulkBatch, bulkResource);
  // the uploaded file can be a spreadsheet of any format, the skipped rows are always a CSV
  const uploadedFilenameWithoutExtension = uploadedFilename.replace(
    /\.[^.]+$/,
    ""
  );

  return `Skipped_rows_${uploadedFilenameWithoutExtension || bulkResource}.csv`;
}

/**
 * Downloads the spreadsheet rows that never made it into the batch, as a CSV.
 *
 * The rows stay in the Flatfile sheet, so they are fetched straight from Flatfile by error state.
 * `filter: "error"` rather than a search on the listener's own uploadStatus column, because that
 * column is only written to rows we actually submitted: rows that failed validation are never sent,
 * never stamped, and a search misses them entirely. Error state covers both - the listener enriches
 * rejected rows with error messages, which is what makes them invalid in Flatfile's eyes too.
 *
 * The download endpoint takes no FFQL `q`, so `filter` is the only lever here; the skipped-row count
 * queries the same state via /counts so the two can never disagree.
 */
export function useDownloadSkippedRows(
  bulkBatch: IBulkBatch,
  bulkResource: BulkResource
): () => Promise<void> {
  const { warning } = useSnackbar();
  const flatfileApiClient = useFlatfileApiClient(bulkBatch);

  return useCallback(async () => {
    try {
      if (!flatfileApiClient) {
        throw new Error("No Flatfile access token on the batch");
      }

      const sheetId = await getFlatfileMainSheetId(
        flatfileApiClient,
        bulkBatch,
        bulkResource
      );
      const csvStream = await flatfileApiClient.sheets.getRecordsAsCsv(
        sheetId,
        { filter: "error" }
      );
      // in the browser the Flatfile client resolves the download as a ReadableStream
      const csvBlob = await new Response(
        csvStream as unknown as ReadableStream
      ).blob();

      downloadFileFromBlob(
        csvBlob,
        getSkippedRowsFilename(bulkBatch, bulkResource)
      );
    } catch (error) {
      console.error("Failed to download skipped rows", error);
      warning("Failed to download the skipped rows. Please try again.");
    }
  }, [flatfileApiClient, bulkBatch, bulkResource, warning]);
}
